<template>
  <div class="kitchen-expense">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>费用管理</h2>
      <div class="header-summary">
        <el-row :gutter="20">
          <el-col :span="4">
            <div class="summary-card">
              <div class="card-title">本月总支出</div>
              <div class="card-amount">¥ {{ monthlyTotal }}</div>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="summary-card">
              <div class="card-title">本月厨房支出</div>
              <div class="card-amount">¥ {{ monthlyKitchen }}</div>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="summary-card">
              <div class="card-title">本月维修支出</div>
              <div class="card-amount">¥ {{ monthlyRepair }}</div>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="summary-card">
              <div class="card-title">本月工资支出</div>
              <div class="card-amount">¥ {{ monthlySalary }}</div>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="summary-card">
              <div class="card-title">本月燃油支出</div>
              <div class="card-amount">¥ {{ monthlyFuel }}</div>
            </div>
          </el-col>
          <el-col :span="4">
            <div class="summary-card">
              <div class="card-title">本月其他支出</div>
              <div class="card-amount">¥ {{ monthlyOther }}</div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 操作栏 -->
    <div class="operation-bar">
      <div class="left-operations">
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增费用</el-button>
        <el-button type="el-icon-download" icon="el-icon-download" @click="handleExport('other')">导出数据</el-button>
        <el-button type="el-icon-download" icon="el-icon-download" @click="handleExport('repair')">导出外聘维修费用表</el-button>
      </div>
      <div class="right-operations">
        <el-select
          v-model="filterType"
          placeholder="费用类型"
          clearable
          style="width: 120px; margin-right: 10px"
          @change="handleTypeChange"
        >
          <el-option
            v-for="item in typeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-select
          v-model="expensePerson"
          placeholder="报销人"
          clearable
          filterable
          allow-create
          default-first-option
          style="width: 120px; margin-right: 10px"
          @change="handleFilter"
        >
          <el-option
            v-for="item in expensePersonOptions"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="width: 330px; margin-right: 10px"
          @change="handleDateChange"
        />
        <el-input
          v-model="searchKeyword"
          placeholder="搜索备注"
          prefix-icon="el-icon-search"
          style="width: 200px"
          @input="handleSearch"
        />
      </div>
    </div>

    <!-- 表格 -->
    <el-card class="table-card" shadow="hover">
      <el-table
        :data="tableData"
        border
        stripe
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa' }"
      >
        <el-table-column prop="date" label="日期" width="120">
          <template #default="scope">
            {{ formatDate(scope.row.date) }}
          </template>
        </el-table-column>
        <el-table-column prop="type" label="费用类型" width="120">
          <template #default="scope">
            <el-tag :type="getTagType(scope.row.type)">
              {{ typeMap[scope.row.type] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="carNumber" label="车号" width="120"/>
        <el-table-column prop="expensePerson" label="报销人" width="120"/>
        <el-table-column prop="amount" label="金额" width="150">
          <template #default="scope">
            <span class="amount">¥ {{ scope.row.amount.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" show-overflow-tooltip/>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="scope">
            <el-button
              type="text"
              icon="el-icon-edit"
              @click="handleEdit(scope.row)"
            >编辑</el-button>
            <el-button
              type="text"
              icon="el-icon-delete"
              class="delete-btn"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogType === 'add' ? '新增费用' : '编辑费用'"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="日期" prop="date">
          <el-date-picker
            v-model="form.date"
            type="date"
            placeholder="选择日期"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="费用类型" prop="type">
          <el-select v-model="form.type" placeholder="请选择费用类型" style="width: 100%">
            <el-option
              v-for="(label, value) in typeMap"
              :key="value"
              :label="label"
              :value="value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="车号" prop="carNumber">
          <el-input
            v-model="form.carNumber"
            placeholder="请输入车号"
          />
        </el-form-item>

        <el-form-item label="费用人" prop="expensePerson">
          <el-select
            v-model="form.expensePerson"
            placeholder="请选择或输入费用人"
            filterable
            allow-create
            default-first-option
            style="width: 100%"
          >
            <el-option
              v-for="item in expensePersonOptions"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="金额" prop="amount">
          <el-input-number
            v-model="form.amount"
            :precision="2"
            :step="0.01"
            style="width: 100%"
            controls-position="right"
            placeholder="请输入金额"
          />
        </el-form-item>

        <el-form-item label="备注" prop="remark">
          <el-input
            v-model="form.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注信息"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { formatDate } from '@/utils/date'
import {
  getKitchenExpenseList,
  addKitchenExpense,
  updateKitchenExpense,
  deleteKitchenExpense
} from '@/api/kitchen'
import moment from 'moment'

export default {
  name: 'Kitchen',
  data() {
    return {
      // 表格数据
      tableData: [],
      searchKeyword: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,

      // 弹窗相关
      dialogVisible: false,
      dialogType: 'add', // add 或 edit
      submitting: false,

      // 表单数据
      form: {
        id: '',
        date: new Date(),
        type: '',
        amount: 0,
        remark: '',
        carNumber: '',
        expensePerson: ''
      },

      // 费用类型映射
      typeMap: {
        kitchen: '厨房',
        repair: '配件维修',
        salary: '工资',
        fuel: '燃油',
        other: '其他'
      },

      // 表单验证规则
      rules: {
        date: [
          { required: true, message: '请选择日期', trigger: 'change' }
        ],
        type: [
          { required: true, message: '请选择费用类型', trigger: 'change' }
        ],
        amount: [
          { required: true, message: '请输入金额', trigger: 'blur' },
          { type: 'number', message: '请输入有效金额', trigger: 'blur' }
        ],
        remark: [
          { max: 200, message: '备注最多200字', trigger: 'blur' }
        ],
        carNumber: [
          { required: true, message: '请输入车号', trigger: 'blur' },
          { max: 50, message: '车号最多50个字符', trigger: 'blur' }
        ],
        expensePerson: [
          { required: true, message: '请输入费用人', trigger: 'blur' },
          { max: 50, message: '费用人最多50个字符', trigger: 'blur' }
        ]
      },

      filterType: '',
      dateRange: [
        moment().startOf('month').format('YYYY-MM-DD'),
        moment().endOf('month').format('YYYY-MM-DD')
      ],
      monthlyTotal: 0.00,
      monthlyKitchen: 0.00,
      monthlyRepair: 0.00,
      monthlySalary: 0.00,
      monthlyFuel: 0.00,
      monthlyOther: 0.00,
      typeOptions: [
        { value: 'kitchen', label: '厨房支出' },
        { value: 'repair', label: '维修支出' },
        { value: 'salary', label: '工资支出' },
        { value: 'fuel', label: '燃油支出' },
        { value: 'other', label: '其他支出' }
      ],
      listQuery: {
        current: 1,
        size: 10,
        type: '',
        startTime: '',
        endTime: '',
        keyword: '',
        expensePerson: ''
      },
      // 添加费用人选项
      expensePersonOptions: ['范占清', '常鹏飞', '王超', '肖洋'],
      expensePerson: '', // 添加报销人选择
    }
  },

  created() {
    // 确保在获取数据前已设置默认日期范围
    this.listQuery.startTime = this.dateRange[0]
    this.listQuery.endTime = this.dateRange[1]
    this.fetchData()
  },

  watch: {
    'listQuery.type'() {
      this.handleFilter()
    },
    dateRange() {
      this.handleFilter()
    }
  },

  methods: {
    formatDate,

    // 获取列表数据
    async fetchData() {
      try {
        const res = await getKitchenExpenseList({
          page: this.currentPage,
          pageSize: this.pageSize,
          type: this.filterType,
          dateRange: this.dateRange,
          keyword: this.searchKeyword,
          expensePerson: this.expensePerson
        })

        if (res.code === 200) {
          this.tableData = res.data.list
          this.total = res.data.total
          // 更新统计数据
          this.monthlyTotal = res.data.monthlyTotal
          this.monthlyKitchen = res.data.monthlyKitchen
          this.monthlyRepair = res.data.monthlyRepair
          this.monthlySalary = res.data.monthlySalary
          this.monthlyFuel = res.data.monthlyFuel
          this.monthlyOther = res.data.monthlyOther
        } else {
          this.$message.error(res.message || '获取数据失败')
        }
      } catch (error) {
        this.$message.error('获取数据失败')
        console.error(error)
      }
    },

    // 搜索
    handleSearch() {
      this.listQuery.type = this.filterType
      this.listQuery.startTime = this.dateRange ? this.dateRange[0] : ''
      this.listQuery.endTime = this.dateRange ? this.dateRange[1] : ''
      this.listQuery.expensePerson = this.expensePerson
      this.fetchData()
    },

    // 分页
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchData()
    },

    // 新增费用
    handleAdd() {
      this.dialogType = 'add'

      this.form = {
        id: '',
        date: new Date(),
        type: '',
        amount: 0,
        remark: '',
        carNumber: '',
        expensePerson: ''
      }
      this.dialogVisible = true
      // 等待DOM更新后设置焦点
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },

    // 编辑费用
    handleEdit(row) {
      this.dialogType = 'edit'
      this.form = {
        id: row.id,
        date: new Date(row.date),
        type: row.type,
        amount: row.amount,
        remark: row.remark,
        carNumber: row.carNumber,
        expensePerson: row.expensePerson
      }
      this.dialogVisible = true
      // 等待DOM更新后设置焦点
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.submitting = true
        try {
          const submitData = {
            ...this.form,
            date: formatDate(this.form.date)
          }

          let res
          if (this.dialogType === 'add') {
            res = await addKitchenExpense(submitData)
          } else {
            res = await updateKitchenExpense(submitData)
          }

          if (res.code === 200) {
            this.$message.success(this.dialogType === 'add' ? '添加成功' : '更新成功')
            this.dialogVisible = false
            this.fetchData()
          } else {
            this.$message.error(res.message || (this.dialogType === 'add' ? '添加失败' : '更新失败'))
          }
        } catch (error) {
          this.$message.error(this.dialogType === 'add' ? '添加失败' : '更新失败')
          console.error(error)
        } finally {
          this.submitting = false
        }
      })
    },

    // 删除费用
    handleDelete(row) {
      this.$confirm('确认删除该费用记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteKitchenExpense(row.id)
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.fetchData()
          } else {
            this.$message.error(res.message || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败')
          console.error(error)
        }
      }).catch(() => {
        // 取消删除，不做处理
      })
    },

    getTagType(type) {
      const typeMap = {
        kitchen: 'success',
        repair: 'warning',
        salary: 'primary',
        fuel: 'danger',
        other: 'info'
      }
      return typeMap[type]
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.fetchData()
    },

    handleExport(exportType) {
      // 构建查询参数
      const params = {
        type: this.filterType || '',
        startTime: this.dateRange && this.dateRange[0] ? this.formatDate(this.dateRange[0]) : '',
        endTime: this.dateRange && this.dateRange[1] ? this.formatDate(this.dateRange[1]) : '',
        exportType: exportType,
        expensePerson: this.expensePerson || '' // 添加报销人参数
      }

      // 构建下载URL
      const queryString = Object.keys(params)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join('&')

      // 使用window.open下载文件
      window.open(`${process.env.VUE_APP_BASE_API}/kitchen/expense/export?${queryString}`)
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('YYYY-MM-DD')
    },

    handleFilter() {
      this.listQuery.current = 1
      this.listQuery.startTime = this.dateRange ? this.dateRange[0] : ''
      this.listQuery.endTime = this.dateRange ? this.dateRange[1] : ''
      this.listQuery.expensePerson = this.expensePerson
      this.fetchData()
    },

    handleTypeChange(val) {
      this.listQuery.type = val
      this.handleFilter()
    },

    handleDateChange(val) {
      if (val) {
        this.listQuery.startTime = val[0]
        this.listQuery.endTime = val[1]
      } else {
        this.listQuery.startTime = ''
        this.listQuery.endTime = ''
      }
      this.handleFilter()
    }
  }
}
</script>

<style lang="scss" scoped>
.kitchen-expense {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 40px);

  .page-header {
    margin-bottom: 24px;

    h2 {
      margin: 0 0 16px;
      color: #1f2f3d;
      font-weight: 500;
    }

    .header-summary {
      margin-top: 20px;
    }

    .summary-card {
      background: #fff;
      padding: 15px;
      border-radius: 4px;
      box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
      height: 100px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .card-title {
        color: #909399;
        font-size: 14px;
        margin-bottom: 8px;
      }

      .card-amount {
        color: #303133;
        font-size: 20px;
        font-weight: bold;
      }
    }
  }

  .operation-bar {
    background: #fff;
    padding: 16px;
    border-radius: 4px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  }

  .table-card {
    margin-bottom: 20px;
  }

  .amount {
    color: #67c23a;
    font-weight: bold;
  }

  .delete-btn {
    color: #f56c6c;
  }

  .pagination-container {
    padding: 15px 0;
    text-align: right;
  }

  :deep(.el-input-number) {
    .el-input__inner {
      text-align: left;
    }
  }

  :deep(.el-tag) {
    text-align: center;
    min-width: 60px;
  }
}
</style>
